import { useId } from 'react';
import type { ReactNode } from 'react';
import { Tooltip } from 'antd';
import { Lock, RotateCcw, Undo2 } from 'lucide-react';

import { Badge, Button, Field, Label } from '../../../../components/ui';
import { useT } from '../../../../i18n';
import { KHOA_CO_CAU_HAU_QUA, tongNguon } from '../../constants';
import { bienHieuLuc, datOTangNay, giaTriHienTai, laSanKeThua, moTaNguon } from '../../nghiep-vu/cai-dat';
import { useDichCaiDat } from '../../hooks/useDichCaiDat';
import type { FormCaiDat } from '../../hooks/useFormCaiDat';
import type { SettingDefinition } from '../../types';
import { DieuKhien } from './DieuKhien';

/**
 * Kiểu cần cả bề ngang của khối: bảng, danh sách thẻ, tuyến thông báo, nút chọn
 * nhiều phương án. Các kiểu còn lại (công tắc, ô số, ô ngày, ô chọn) nằm gọn ở
 * cột phải, nên mọi ô nhập của một khối thẳng một hàng dọc.
 */
function laDieuKhienRong(def: SettingDefinition): boolean {
  switch (def.type) {
    case 'cidr_list':
    case 'notify_route':
    case 'alert_rule_list':
    case 'integer_list':
    case 'string_list':
      return true;
    case 'enum':
      return (def.values ?? []).length <= 3;
    default:
      return false;
  }
}

/**
 * Một tham số của form cài đặt, dựng hoàn toàn từ danh mục (ADR-0059 mục 8).
 *
 * Bố cục hai cột từ `lg` trở lên: **trái** là nhãn, mô tả và nguồn giá trị —
 * thứ người dùng đọc trước khi sửa ("ai đặt, sửa ở đây thì đè lên cái gì");
 * **phải** là ô nhập kèm đơn vị, biên hợp lệ ngay dưới ô và nút "Về mặc định".
 * Màn hẹp hơn thì xếp một cột theo đúng thứ tự đó. Kiểu cần bề ngang
 * (`laDieuKhienRong`) đặt ô nhập dưới phần chữ, nút hoàn tác lên góc phải.
 *
 * Dòng chỉ xem vẫn hiện giá trị **đúng chỗ** ô nhập, trong khung chỉ đọc có
 * khoá và lý do (tooltip, kèm chữ ẩn cho trình đọc màn hình).
 *
 * Lỗi đi qua `<Field error>`, nên nó **thay** dòng biên — hiện cả hai thì người
 * dùng phải đoán dòng nào nói về sai sót của mình.
 */
export function DongCaiDat({
  form,
  khoa,
  ipHienTai = null,
  phu,
}: {
  form: FormCaiDat;
  khoa: string;
  ipHienTai?: string | null;
  /** Nội dung thêm dưới ô nhập, ví dụ nút "Gửi thử". */
  phu?: ReactNode;
}) {
  const { t } = useT();
  const d = useDichCaiDat();
  const id = useId();
  const nhanId = `${id}-nhan`;
  const moTaId = `${id}-mo`;
  const lyDoId = `${id}-ly-do`;
  const def = form.danhMuc.get(khoa);
  const m = form.policy?.settings.find((x) => x.key === khoa);
  if (!def || !m || !form.policy) return null;

  const nhan = d.nhan(khoa);
  // Chỉ vài tham số có một câu hậu quả dưới nhãn — danh sách và lý do ở `KHOA_CO_CAU_HAU_QUA`.
  const moTa = KHOA_CO_CAU_HAU_QUA.has(khoa) ? d.moTa(khoa) : undefined;
  const nguon = moTaNguon(m, form.nhap, form.policy.plan_code);
  const giaTri = giaTriHienTai(m, form.nhap);
  const goi = form.policy.plan_code;
  const bien = bienHieuLuc(def, goi);
  const phamVi = form.policy.scope;
  const chuoiBien = d.bien(def, m.bounds ?? bien, bien.theo_goi, laSanKeThua(def, phamVi));
  const loi = form.loi[khoa];
  const suaDuoc = m.editable && !form.chiDoc;
  const coNhap = nguon.loai === 'dang_sua' || nguon.loai === 've_ke_thua';
  const coTheVeMacDinh = suaDuoc && datOTangNay(m, form.nhap, form.policy.scope);
  // Danh sách quy tắc đọc được cả khi không sửa được: một dòng "3 quy tắc" không nói gì.
  const bangChiDoc = !suaDuoc && def.type === 'alert_rule_list';
  const rong = laDieuKhienRong(def);
  // Tham số không đặt được ở tầng đang xem (ví dụ brute force ở cổng tổ chức):
  // nền tảng quản lý — nói thẳng ai quyết, thay cho câu chung về vai trò.
  // Giá trị bị khoá khác ép (ADR-0099) thì nói lý do đó, kể cả với người có quyền sửa.
  const lyDoKhoa = t(
    m.forced_by
      ? 'caiDat.khoaLyDo.biEpEmailNgoaiDomain'
      : form.chiDoc
        ? 'caiDat.khoaLyDo.khongQuyen'
        : def.scopes.includes(phamVi)
          ? 'caiDat.khoaLyDo.tangKhac'
          : 'caiDat.khoaLyDo.nenTangQuanLy',
  );
  const nguCanhNguon = { phamVi, def };

  const nutHoanTac = (coNhap || coTheVeMacDinh) && (
    <div className="flex flex-wrap gap-1">
      {coNhap && (
        <Button variant="ghost" size="sm" icon={<Undo2 size={13} />} onClick={() => form.boNhap(khoa)}>
          {t('caiDat.hoanTac')}
        </Button>
      )}
      {coTheVeMacDinh && (
        <Button
          variant="ghost"
          size="sm"
          icon={<RotateCcw size={13} />}
          title={t('caiDat.veMacDinhMo', {
            nguon: d.nguon(moTaNguon({ ...m, source: m.inherited_source }, {}, goi), nguCanhNguon),
            giaTri: d.giaTri(def, m.inherited_value),
          })}
          onClick={() => form.veMacDinh(khoa)}
        >
          {t('caiDat.veMacDinh')}
        </Button>
      )}
    </div>
  );

  const phanChu = (
    <div className="min-w-0 space-y-1">
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
        <Label id={nhanId} htmlFor={id} className="text-content-primary">
          {nhan}
        </Label>
        <Badge variant={tongNguon(coNhap, m.source === phamVi)}>{d.nguon(nguon, nguCanhNguon)}</Badge>
      </div>
      {moTa && (
        <p id={moTaId} className="max-w-prose text-xs leading-relaxed text-content-muted">
          {moTa}
        </p>
      )}
    </div>
  );

  const phanNhap = (
    // Ô danh sách IP tự đếm dòng trên trần ngay dưới ô nhập: không lặp biên ở đây
    <Field
      id={id}
      hint={suaDuoc && def.type !== 'cidr_list' ? chuoiBien : undefined}
      // Ô danh sách IP đã nói lỗi từng dòng ngay dưới ô ("Dòng 4: …"): không lặp thêm câu đếm dòng sai ở đây
      error={loi && !(loi.kieu === 'giao-dien' && loi.loi.ma === 'dong_ip_sai') ? d.loi(loi, def) : undefined}
    >
      {(p) =>
        suaDuoc || bangChiDoc ? (
          <DieuKhien
            def={def}
            giaTri={giaTri}
            onDoi={(v) => form.doi(khoa, v)}
            chiDoc={bangChiDoc || form.dangLuu}
            p={p}
            ipHienTai={ipHienTai}
            bien={m.bounds ?? bien}
            loiMayChu={form.loiMayChu}
            daLuu={m.value}
          />
        ) : (
          <Tooltip title={lyDoKhoa}>
            <span
              id={p.id}
              role="textbox"
              aria-readonly
              aria-labelledby={nhanId}
              aria-describedby={lyDoId}
              className="relative inline-flex min-h-8 max-w-full items-center gap-2 rounded border border-default bg-subtle px-3 py-1 text-sm font-medium tabular-nums text-content-primary"
            >
              <Lock size={13} className="shrink-0 text-content-muted" aria-hidden />
              <span className="min-w-0 break-words">{d.giaTri(def, giaTri)}</span>
              <span id={lyDoId} className="sr-only">
                {lyDoKhoa}
              </span>
            </span>
          </Tooltip>
        )
      }
    </Field>
  );

  if (rong) {
    return (
      <div className="space-y-3 py-4 first:pt-0 last:pb-0">
        <div className="flex min-w-0 flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          {phanChu}
          {nutHoanTac && <div className="shrink-0">{nutHoanTac}</div>}
        </div>
        {phanNhap}
        {phu}
      </div>
    );
  }

  return (
    <div className="grid gap-x-8 gap-y-3 py-4 first:pt-0 last:pb-0 lg:grid-cols-[minmax(0,1fr)_20rem]">
      {phanChu}
      <div className="min-w-0 space-y-2">
        {phanNhap}
        {/* Bị ép thì lý do hiện thành chữ, không chỉ trong tooltip: người dùng cần biết sửa ở đâu. */}
        {m.forced_by && <p className="text-xs leading-relaxed text-content-secondary">{lyDoKhoa}</p>}
        {/* Nút ghost có lề trong: kéo sang trái cho chữ thẳng mép ô nhập. */}
        {nutHoanTac && <div className="-ml-2">{nutHoanTac}</div>}
        {phu}
      </div>
    </div>
  );
}
